import { useState, useEffect, useRef, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  getCampaignById,
  getCampaignMessages,
  downloadCampaignReport,
} from "../../actions/campaigns/campaigns";
import {
  CampaignMessagesTable,
  TemplateDetailPreview,
} from "../../components/apps/campaigns";
import { CAMPAIGN_STATUS_LABELS } from "../../components/apps/campaigns/constants";
import { formatDateTime } from "../../utils/dateFormatters-campaign.utils";
import { downloadReportFile } from "../../utils/downloadReportFile";
import { ArrowBackIos, Download, Refresh } from "@mui/icons-material";
import { getTemplateById } from "../../actions/templates";
import { useToken } from "../../hooks/useToken";
import { SocketContext } from "../../context/SocketContext";

export const CampaignDetailsPage = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();

  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [template, setTemplate] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "error",
    message: "",
  });

  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesPage, setMessagesPage] = useState(1);
  const [messagesPageSize, setMessagesPageSize] = useState(10);
  const [messagesTotal, setMessagesTotal] = useState(0);
  const [phoneSearch, setPhoneSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [downloading, setDownloading] = useState(false);
  const [reportRequested, setReportRequested] = useState(false);

  const searchTimerRef = useRef(null);

  const { name: username, gupshup_app_id: apiKey, id_customer } = useSelector(
    (state) => state.agent
  );
  const { getTokenSilent } = useToken();
  const { socketGateway } = useContext(SocketContext);

  useEffect(() => {
    if (username && apiKey && campaignId) {
      loadCampaignDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, username, apiKey]);

  useEffect(() => {
    if (campaign) {
      loadMessages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messagesPage, messagesPageSize, statusFilter]);

  useEffect(() => {
    if (!campaign) return;

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    searchTimerRef.current = setTimeout(() => {
      setMessagesPage(1); 
      loadMessages();
    }, 500);

    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneSearch]);

  const loadCampaignDetails = async () => {
    setLoading(true);

    try {
      const data = await getCampaignById({
        campaignId,
        username,
        apiKey,
      });

      setCampaign(data);

      // Fetch template details if campaign has a template_id
      if (data?.template_id) {
        setTemplateLoading(true);
        try {
          const token = await getTokenSilent();
          const templateData = await getTemplateById(data.template_id, token);
          if (templateData) {
            setTemplate(templateData);
          }
        } catch (tokenError) {
          console.warn("No se pudo obtener el token o el template:", tokenError);
        } finally {
          setTemplateLoading(false);
        }
      }

      await loadMessages();
    } catch (error) {
      console.error("Error al cargar campaña:", error);
      showSnackbar("Error al cargar los detalles de la campaña", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    if (!campaignId) return;

    setMessagesLoading(true);

    try {
      const result = await getCampaignMessages({
        campaignId,
        username,
        apiKey,
        page: messagesPage,
        pageSize: messagesPageSize,
        phoneNumber: phoneSearch,
        status: statusFilter,
      });

      setMessages(result.items);
      setMessagesTotal(result.total);
    } catch (error) {
      console.error("Error al cargar mensajes:", error);
      setMessages([]);
      showSnackbar("Error al cargar los mensajes de la campaña", "error");
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    setDownloading(true);

    try {
      const data = await downloadCampaignReport({
        campaignId,
        username,
        apiKey,
      });

      if (data.success) {
        setReportRequested(true);
        showSnackbar(
          "El reporte se está generando. Te avisaremos cuando esté listo.",
          "info"
        );
      } else {
        showSnackbar(
          data.message || "El reporte no pudo generarse",
          "error"
        );
      }
    } catch (error) {
      showSnackbar(
        error.message || "Error al solicitar el reporte",
        "error"
      );
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadReadyReport = async () => {
    try {
      await downloadReportFile({ reportUrl: campaign.report_url, campaignId });
    } catch (error) {
      showSnackbar("Error al descargar el reporte", "error");
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, severity, message });
  };

  useEffect(() => {
    if (!socketGateway) return;

    const onCampaignReportReady = (payload) => {
      console.log("[campaign_report_ready] recibido:", payload);
      const raw = payload?.payload || payload;
      const reportUrl = raw?.report_url;

      if (reportUrl) {
        setReportRequested(false);
        setCampaign((prev) => (prev ? { ...prev, report_url: reportUrl } : prev));
        showSnackbar("El reporte está listo para descargar", "success");
      }
    };

    socketGateway.on("campaign_report_ready", onCampaignReportReady);
    return () => socketGateway.off("campaign_report_ready", onCampaignReportReady);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socketGateway, campaignId]);

  const handleBack = () => {
    navigate("/admin/campaigns");
  };

  const handlePhoneSearchChange = (value) => {
    setPhoneSearch(value);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setMessagesPage(1);
  };

  const handleMessagesPageChange = (newPage) => {
    setMessagesPage(newPage);
  };

  const handleMessagesPageSizeChange = (newPageSize) => {
    setMessagesPageSize(newPageSize);
    setMessagesPage(1);
  };

  if (loading) {
    return (
      <Container
        maxWidth="lg"
        sx={{ mt: 4, display: "flex", justifyContent: "center" }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (!campaign) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography variant="h6" color="text.secondary">
          Campaña no encontrada
        </Typography>
        <Button
          startIcon={<ArrowBackIos />}
          onClick={handleBack}
          sx={{ mt: 2 }}
        >
          Volver a campañas
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4, display: "flex", alignItems: "center", gap: 2 }}>
        <Button startIcon={<ArrowBackIos />} onClick={handleBack}>
          Volver
        </Button>
        <Typography variant="h4" component="h1">
          Detalles de Campaña
        </Typography>
        <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
          {campaign.report_url && (
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={handleDownloadReadyReport}
            >
              Descargar reporte
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleDownloadReport}
            disabled={downloading || reportRequested}
          >
            {downloading
              ? "Solicitando..."
              : reportRequested
              ? "Generando reporte..."
              : campaign.report_url
              ? "Regenerar reporte"
              : "Descargar reporte"}
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Información General
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr 380px' },
                gap: 2.5,
                mt: 1,
              }}
            >
              {/* Left column: basic info */}
              <Box>
                <Box sx={{ mb: 2.5 }}>
                  <Typography variant="body2" color="text.secondary">ID</Typography>
                  <Typography variant="body1">{campaign.id}</Typography>
                </Box>

                <Box sx={{ mb: 2.5 }}>
                  <Typography variant="body2" color="text.secondary">Nombre</Typography>
                  <Typography variant="body1">{campaign.name}</Typography>
                </Box>

                <Box sx={{ mb: 2.5 }}>
                  <Typography variant="body2" color="text.secondary">Estado</Typography>
                  <Chip
                    label={CAMPAIGN_STATUS_LABELS[campaign.status] || campaign.status}
                    color={campaign.status === "scheduled" ? "primary" : "default"}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Box>

                <Box sx={{ mb: 2.5 }}>
                  <Typography variant="body2" color="text.secondary">Fecha de Programación</Typography>
                  <Typography variant="body1">
                    {campaign.programming_date
                      ? formatDateTime(new Date(campaign.programming_date))
                      : "-"}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2.5 }}>
                  <Typography variant="body2" color="text.secondary">Fecha de Creación</Typography>
                  <Typography variant="body1">
                    {campaign.created_at
                      ? formatDateTime(new Date(campaign.created_at))
                      : "-"}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">Total de Contactos</Typography>
                  <Typography variant="body1">{campaign.total_contacts || 0}</Typography>
                </Box>
              </Box>

              {/* Center column: template selection, upload type */}
              <Box>
                <Box sx={{ mb: 2.5 }}>
                  <Typography variant="body2" color="text.secondary">Template</Typography>
                  {template ? (
                    <Typography variant="body1">{template.name}</Typography>
                  ) : templateLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <CircularProgress size={14} />
                      <Typography variant="body2" color="text.secondary">Cargando...</Typography>
                    </Box>
                  ) : (
                    <Typography variant="body1">{campaign.template_id || "-"}</Typography>
                  )}
                </Box>

                {campaign.template_segments && campaign.template_segments.length > 0 && (
                  <Box sx={{ mb: 2.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Templates clonados ({campaign.template_segments.length + 1} en total)
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Bloque</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Template ID</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {[
                            { segment_order: 0, template_id: campaign.template_id, name: campaign.template_name || "Template original" },
                            ...campaign.template_segments,
                          ].map((seg, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{seg.segment_order + 1}</TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                                  {seg.template_id}
                                </Typography>
                              </TableCell>
                              <TableCell>{seg.name || "-"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                <Box sx={{ mb: 2.5 }}>
                  <Typography variant="body2" color="text.secondary">Tipo de Carga</Typography>
                  <Typography variant="body1">
                    {campaign.upload_contacts_type === "file" ? "Archivo" : "Contactos"}
                  </Typography>
                </Box>

                {campaign.upload_contacts_file && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Archivo de Contactos</Typography>
                    <Typography
                      variant="body2"
                      component="a"
                      href={campaign.upload_contacts_file}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: "primary.main", textDecoration: "none" }}
                    >
                      Ver archivo
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Right column: template preview */}
              <Box sx={{ position: { lg: 'sticky' }, top: { lg: 0 }, alignSelf: 'start' }}>
                {templateLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">Cargando template...</Typography>
                  </Box>
                ) : template ? (
                  <TemplateDetailPreview template={template} />
                ) : (
                  <Box sx={{ p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Sin preview disponible</Typography>
                    <Typography variant="caption" color="text.disabled">{campaign.template_id || ""}</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Paper>
        </Grid>

        {campaign.message_events && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Estadísticas
              </Typography>

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    Enviados
                  </Typography>
                  <Typography variant="h5">
                    {campaign.message_events.sent || 0}
                  </Typography>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    Entregados
                  </Typography>
                  <Typography variant="h5">
                    {campaign.message_events.delivered || 0}
                  </Typography>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    Leídos
                  </Typography>
                  <Typography variant="h5">
                    {campaign.message_events.read || 0}
                  </Typography>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    Fallidos
                  </Typography>
                  <Typography variant="h5" color="error">
                    {campaign.message_events.failed || 0}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        )}

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Información Contactos
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Historial de mensajes enviados a los contactos de esta campaña
            </Typography>

            <CampaignMessagesTable
              messages={messages}
              loading={messagesLoading}
              phoneSearch={phoneSearch}
              statusFilter={statusFilter}
              onPhoneSearchChange={handlePhoneSearchChange}
              onStatusFilterChange={handleStatusFilterChange}
              page={messagesPage}
              pageSize={messagesPageSize}
              total={messagesTotal}
              onPageChange={handleMessagesPageChange}
              onPageSizeChange={handleMessagesPageSizeChange}
            />
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};
